import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { tokenStorage } from '../services/api';
import { toast } from 'sonner';
import { 
  ClipboardList, 
  Search, 
  Filter,
  Clock,
  CheckCircle2,
  XCircle,
  Truck,
  AlertTriangle,
  MapPin,
  User,
  Calendar,
  ChevronDown,
  Eye,
  Phone,
  Building,
  Zap,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Durum tanımları
const STATUS_CONFIG = {
  pending: { 
    label: 'Bekliyor', 
    color: 'bg-yellow-500', 
    lightColor: 'bg-yellow-500/10', 
    textColor: 'text-yellow-500',
    icon: Clock 
  },
  assigned: { 
    label: 'Atandı', 
    color: 'bg-yellow-600', 
    lightColor: 'bg-yellow-600/10', 
    textColor: 'text-yellow-600',
    icon: Clock 
  },
  accepted: { 
    label: 'Kabul Edildi', 
    color: 'bg-blue-500', 
    lightColor: 'bg-blue-500/10', 
    textColor: 'text-blue-500',
    icon: CheckCircle2 
  },
  en_route: { 
    label: 'Yolda', 
    color: 'bg-purple-500', 
    lightColor: 'bg-purple-500/10', 
    textColor: 'text-purple-500',
    icon: Truck 
  },
  arrived: { 
    label: 'Vardı', 
    color: 'bg-indigo-500', 
    lightColor: 'bg-indigo-500/10', 
    textColor: 'text-indigo-500',
    icon: MapPin 
  },
  in_progress: { 
    label: 'Devam Ediyor', 
    color: 'bg-cyan-500', 
    lightColor: 'bg-cyan-500/10', 
    textColor: 'text-cyan-500',
    icon: RefreshCw 
  },
  completed: { 
    label: 'Tamamlandı', 
    color: 'bg-green-500', 
    lightColor: 'bg-green-500/10', 
    textColor: 'text-green-500',
    icon: CheckCircle2 
  },
  closed: { 
    label: 'Kapatıldı', 
    color: 'bg-emerald-600', 
    lightColor: 'bg-emerald-600/10', 
    textColor: 'text-emerald-600',
    icon: CheckCircle2 
  },
  rejected: { 
    label: 'Reddedildi', 
    color: 'bg-red-500', 
    lightColor: 'bg-red-500/10', 
    textColor: 'text-red-500',
    icon: XCircle 
  },
  cancelled: { 
    label: 'İptal', 
    color: 'bg-gray-500', 
    lightColor: 'bg-gray-500/10', 
    textColor: 'text-gray-500',
    icon: XCircle 
  },
  pending_approval: { 
    label: 'Onay Bekliyor', 
    color: 'bg-amber-500', 
    lightColor: 'bg-amber-500/10', 
    textColor: 'text-amber-500',
    icon: Clock 
  },
  approved: { 
    label: 'Onaylandı', 
    color: 'bg-teal-500', 
    lightColor: 'bg-teal-500/10', 
    textColor: 'text-teal-500',
    icon: CheckCircle2 
  },
  payment_pending: { 
    label: 'Ödeme Bekliyor', 
    color: 'bg-orange-500', 
    lightColor: 'bg-orange-500/10', 
    textColor: 'text-orange-500',
    icon: Clock 
  },
  paid: { 
    label: 'Ödendi', 
    color: 'bg-green-600', 
    lightColor: 'bg-green-600/10', 
    textColor: 'text-green-600',
    icon: CheckCircle2 
  }
};

// Öncelik tanımları
const PRIORITY_CONFIG = {
  low: { label: 'Düşük', color: 'bg-gray-400', textColor: 'text-gray-400' },
  medium: { label: 'Orta', color: 'bg-yellow-500', textColor: 'text-yellow-500' },
  high: { label: 'Yüksek', color: 'bg-orange-500', textColor: 'text-orange-500' },
  urgent: { label: 'Acil', color: 'bg-red-500', textColor: 'text-red-500' }
};

// Durum akış kuralları - her durum için izin verilen sonraki durumlar
const STATUS_FLOW = {
  pending: ['accepted', 'rejected', 'cancelled'],     // Bekliyor -> Kabul Edildi, Reddedildi, İptal
  assigned: ['accepted', 'rejected', 'cancelled'],    // Atandı -> Kabul Edildi, Reddedildi, İptal
  accepted: ['en_route', 'cancelled'],                // Kabul Edildi -> Yolda, İptal
  en_route: ['arrived', 'cancelled'],                 // Yolda -> Vardı, İptal
  arrived: ['in_progress', 'cancelled'],              // Vardı -> Devam Ediyor, İptal
  in_progress: ['completed', 'cancelled'],            // Devam Ediyor -> Tamamlandı, İptal
  completed: ['pending_approval'],                    // Tamamlandı -> Onay Bekliyor
  pending_approval: ['approved', 'rejected'],         // Onay Bekliyor -> Onaylandı, Reddedildi
  approved: ['payment_pending'],                      // Onaylandı -> Ödeme Bekliyor
  payment_pending: ['paid'],                          // Ödeme Bekliyor -> Ödendi (admin pay butonu)
  paid: [],                                           // Ödendi -> son durum
  closed: [],                                         // Kapatıldı -> son durum
  rejected: [],                                       // Reddedildi -> son durum
  cancelled: []                                       // İptal -> son durum
};

// Sadece admin yapabilecek durumlar
const ADMIN_ONLY_STATUSES = ['closed', 'rejected', 'cancelled'];

// Tarih formatlama
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('tr-TR', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Detay Modal
const WorkOrderDetailModal = ({ workOrder, isOpen, onClose, onStatusChange, onPayment, userRole }) => {
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  if (!isOpen || !workOrder) return null;

  const statusConfig = STATUS_CONFIG[workOrder.status] || STATUS_CONFIG.pending;
  const priorityConfig = PRIORITY_CONFIG[workOrder.priority] || PRIORITY_CONFIG.medium;
  const StatusIcon = statusConfig.icon;
  
  // Kullanıcı admin mi?
  const isAdmin = ['super_admin', 'admin'].includes(userRole);
  
  // Ödeme yapılabilir mi? (approved, payment_pending durumlarında admin yapabilir)
  const canPay = isAdmin && ['approved', 'payment_pending', 'pending_approval'].includes(workOrder.status);
  
  // Mevcut duruma göre izin verilen sonraki durumlar
  const allowedStatuses = STATUS_FLOW[workOrder.status] || [];
  
  // Role göre filtreleme - admin değilse admin-only durumları çıkar
  const availableStatuses = allowedStatuses.filter(status => {
    if (ADMIN_ONLY_STATUSES.includes(status) && !isAdmin) {
      return false;
    }
    return true;
  });

  const handleStatusChange = async () => {
    if (!newStatus) return;
    setIsUpdating(true);
    await onStatusChange(workOrder.id, newStatus, notes);
    setIsUpdating(false);
    setNewStatus('');
    setNotes('');
  };

  // Son durumlar için güncelleme bölümünü gizle
  const isFinalStatus = ['closed', 'cancelled', 'rejected', 'paid'].includes(workOrder.status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-2xl w-full max-w-2xl mx-4 shadow-2xl border border-border max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${statusConfig.lightColor}`}>
              <StatusIcon className={`w-5 h-5 ${statusConfig.textColor}`} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{workOrder.title}</h2>
              <p className="text-sm text-muted-foreground">İş Emri #{workOrder.id?.slice(-8)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <XCircle className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Status & Priority */}
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig.lightColor} ${statusConfig.textColor}`}>
              {statusConfig.label}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium bg-opacity-10 ${priorityConfig.textColor}`} 
                  style={{backgroundColor: `${priorityConfig.color}20`}}>
              {priorityConfig.label} Öncelik
            </span>
          </div>
          
          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Building className="w-4 h-4" />
                <span>Site</span>
              </div>
              <p className="text-foreground font-medium">{workOrder.site_name || '-'}</p>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Zap className="w-4 h-4" />
                <span>İstasyon</span>
              </div>
              <p className="text-foreground font-medium">{workOrder.station_name || '-'}</p>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <User className="w-4 h-4" />
                <span>Teknisyen</span>
              </div>
              <p className="text-foreground font-medium">{workOrder.technician_name || '-'}</p>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Phone className="w-4 h-4" />
                <span>Telefon</span>
              </div>
              <p className="text-foreground font-medium">{workOrder.technician_phone || '-'}</p>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Calendar className="w-4 h-4" />
                <span>Oluşturulma</span>
              </div>
              <p className="text-foreground font-medium">{formatDate(workOrder.created_at)}</p>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Clock className="w-4 h-4" />
                <span>Tahmini Süre</span>
              </div>
              <p className="text-foreground font-medium">{workOrder.estimated_duration || '-'} dk</p>
            </div>
          </div>
          
          {/* Description */}
          {workOrder.description && (
            <div className="p-3 rounded-lg bg-muted">
              <p className="text-sm font-medium text-muted-foreground mb-1">Açıklama</p>
              <p className="text-foreground">{workOrder.description}</p>
            </div>
          )}
          
          {/* Completion Notes */}
          {workOrder.completion_notes && (
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
              <p className="text-sm font-medium text-green-500 mb-1">Tamamlanma Notu</p>
              <p className="text-foreground">{workOrder.completion_notes}</p>
            </div>
          )}
          
          {/* Status Change Section */}
          {!isFinalStatus && availableStatuses.length > 0 && (
            <div className="p-4 rounded-lg border border-border bg-muted/50">
              <h3 className="text-sm font-medium text-foreground mb-3">Durum Güncelle</h3>
              <p className="text-xs text-muted-foreground mb-3">
                {workOrder.status === 'completed' && isAdmin 
                  ? 'İşi onaylamak için "Kapatıldı" seçin'
                  : 'Sonraki adım için durum seçin'}
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                {availableStatuses.map((statusKey) => {
                  const config = STATUS_CONFIG[statusKey];
                  if (!config) return null;
                  return (
                    <button
                      key={statusKey}
                      onClick={() => setNewStatus(statusKey)}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                        newStatus === statusKey
                          ? `${config.color} text-white`
                          : `${config.lightColor} ${config.textColor} hover:opacity-80`
                      }`}
                    >
                      {config.label}
                    </button>
                  );
                })}
              </div>
              
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Not ekleyin (opsiyonel)..."
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground resize-none mb-3"
                rows={2}
              />
              
              <button
                onClick={handleStatusChange}
                disabled={!newStatus || isUpdating}
                className="w-full py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUpdating ? 'Güncelleniyor...' : 'Durumu Güncelle'}
              </button>
            </div>
          )}
          
          {/* Payment Button - Admin Only */}
          {canPay && (
            <div className="p-4 rounded-lg border-2 border-green-500/50 bg-green-500/10">
              <h3 className="text-sm font-medium text-green-600 mb-2">Ödeme İşlemi</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Bu iş emri için ödeme yapıldıysa "Ödendi Olarak İşaretle" butonuna tıklayın.
              </p>
              <button
                onClick={() => onPayment(workOrder.id)}
                className="w-full py-3 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                data-testid={`pay-btn-${workOrder.id}`}
              >
                <CheckCircle2 className="w-5 h-5" />
                Ödendi Olarak İşaretle
              </button>
            </div>
          )}
          
          {/* Final Status Message */}
          {isFinalStatus && (
            <div className="p-4 rounded-lg bg-muted/50 text-center">
              <p className="text-sm text-muted-foreground">
                Bu iş emri <strong>{statusConfig.label}</strong> durumunda. Durum güncellenemez.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Ana Sayfa
const WorkOrdersPage = () => {
  const { user, getEffectiveRole, getEffectiveSiteId } = useAuth();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Efektif rol kontrolü - simülasyon desteği
  const effectiveRole = getEffectiveRole ? getEffectiveRole() : user?.role;
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : user?.site_id;
  const isEffectiveHesapYoneticisi = effectiveRole === 'hesap_yoneticisi' || effectiveRole === 'site_manager';
  const isEffectiveAdmin = ['super_admin', 'admin'].includes(effectiveRole);

  const getToken = () => tokenStorage.get();

  const fetchWorkOrders = async () => {
    try {
      const token = getToken();
      if (!token) {
        setLoading(false);
        return;
      }
      
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      // Hesap Yöneticisi için site filtresi
      if (isEffectiveHesapYoneticisi && effectiveSiteId) {
        params.append('site_id', effectiveSiteId);
      }
      
      const response = await fetch(`${API_URL}/api/work-orders?${params.toString()}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        let data = await response.json();
        // Ekstra frontend filtresi - Hesap Yöneticisi için
        if (isEffectiveHesapYoneticisi && effectiveSiteId) {
          data = (data || []).filter(wo => wo.site_id === effectiveSiteId);
        }
        setWorkOrders(data || []);
      } else {
        console.error('Work orders fetch failed:', response.status);
      }
    } catch (error) {
      console.error('Work orders fetch error:', error);
      toast.error('İş emirleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkOrders();
  }, [statusFilter, isEffectiveHesapYoneticisi, effectiveSiteId]);

  const handleStatusChange = async (workOrderId, newStatus, notes) => {
    try {
      const token = getToken();
      const params = new URLSearchParams({ status: newStatus });
      if (notes) params.append('notes', notes);
      
      const response = await fetch(`${API_URL}/api/work-orders/${workOrderId}/status?${params.toString()}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success('Durum güncellendi');
        fetchWorkOrders();
        setShowDetailModal(false);
      } else {
        toast.error('Durum güncellenemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  // Ödeme işlemi
  const handlePayment = async (workOrderId) => {
    if (!window.confirm('Bu iş emrini ödendi olarak işaretlemek istediğinize emin misiniz?')) {
      return;
    }
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/work-orders/${workOrderId}/pay`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success('Ödeme kaydedildi');
        fetchWorkOrders();
        setShowDetailModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Ödeme kaydedilemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const openDetail = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  // Filtreleme
  const filteredOrders = workOrders.filter(order => {
    const matchesSearch = 
      order.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.technician_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.site_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.station_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="work-orders-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <ClipboardList className="w-8 h-8 text-primary" />
            İş Emirleri
          </h1>
          <p className="text-muted-foreground mt-1">
            Arıza atamalarından oluşan iş emirlerini yönetin
          </p>
        </div>
        
        <button
          onClick={fetchWorkOrders}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Yenile
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={isEffectiveHesapYoneticisi ? "İş emri veya teknisyen ara..." : "İş emri, teknisyen veya site ara..."}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        
        {/* Status Filter */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              statusFilter === 'all'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-accent'
            }`}
          >
            Tümü
          </button>
          {Object.entries(STATUS_CONFIG).slice(0, 5).map(([key, config]) => (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                statusFilter === key
                  ? `${config.color} text-white`
                  : `${config.lightColor} ${config.textColor} hover:opacity-80`
              }`}
            >
              {config.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-card border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-500/10">
              <Clock className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {workOrders.filter(o => o.status === 'pending').length}
              </p>
              <p className="text-sm text-muted-foreground">Bekleyen</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 rounded-xl bg-card border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Truck className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {workOrders.filter(o => ['en_route', 'arrived', 'in_progress'].includes(o.status)).length}
              </p>
              <p className="text-sm text-muted-foreground">Devam Eden</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 rounded-xl bg-card border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {workOrders.filter(o => o.status === 'completed').length}
              </p>
              <p className="text-sm text-muted-foreground">Tamamlanan</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 rounded-xl bg-card border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <ClipboardList className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{workOrders.length}</p>
              <p className="text-sm text-muted-foreground">Toplam</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      {filteredOrders.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-xl border border-border">
          <ClipboardList className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">İş emri bulunamadı</h3>
          <p className="text-muted-foreground">
            {searchTerm || statusFilter !== 'all' 
              ? 'Arama kriterlerinize uygun iş emri yok' 
              : 'Henüz iş emri oluşturulmamış'}
          </p>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">İş Emri</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Teknisyen</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Site / İstasyon</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Öncelik</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Durum</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Tarih</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => {
                  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                  const priorityConfig = PRIORITY_CONFIG[order.priority] || PRIORITY_CONFIG.medium;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <tr key={order.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-foreground">{order.title}</p>
                          <p className="text-xs text-muted-foreground">#{order.id?.slice(-8)}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{order.technician_name || '-'}</p>
                            <p className="text-xs text-muted-foreground">{order.technician_phone || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="text-sm text-foreground">{order.site_name || '-'}</p>
                          <p className="text-xs text-muted-foreground">{order.station_name || '-'}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${priorityConfig.textColor}`}
                              style={{backgroundColor: `${priorityConfig.color}20`}}>
                          {priorityConfig.label}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.lightColor} ${statusConfig.textColor}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-muted-foreground">{formatDate(order.created_at)}</p>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => openDetail(order)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-sm"
                        >
                          <Eye className="w-4 h-4" />
                          Detay
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <WorkOrderDetailModal
        workOrder={selectedOrder}
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        onStatusChange={handleStatusChange}
        onPayment={handlePayment}
        userRole={user?.role}
      />
    </div>
  );
};

export default WorkOrdersPage;
